import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/system';
import { Divider } from '@mui/material';
import { RackingArea } from '@thive/fddm-types';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { RackingAreaType } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/racking/RackingAreaType';
import RackingLoadHandlingType from '@/modules/salesWorkspace/components/propertyPanel/layout/components/racking/RackingLoadHandlingType';
import Alignment from '@/modules/salesWorkspace/components/propertyPanel/layout/components/racking/Alignment';
import { VehicleType } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/common/VehicleType';
import { OffsetDriving } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/racking/OffsetDriving';
import { Margin } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/racking/Margin';
import { AlongsideExtension } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/racking/AlongsideExtension';
import { InsideExtension } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/racking/InsideExtension';
import { RackingPoiPreferences } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/racking/RackingPoiPreferences';
import { RackingConnectNeighbors } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/racking/RackingConnectNeighbors';
import { RackSpec } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/racking/RackSpec';
import { LoadType } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/singularStation/LoadType';
import { UserPreferenceName } from '@/modules/userPreferences';
import {
  FIELDS_PADDING_BOTTOM,
  FIELDS_SPACING,
  PANEL_ACCORDION_SX,
} from '@/modules/common/constants/propertyPanel';

interface Props {
  selectedRackingAreas: RackingArea[];
}

function RackingAreaParametersComponent({ selectedRackingAreas }: Props) {
  const { t } = useTranslation();
  const title =
    selectedRackingAreas.length === 1
      ? t<string>('interface:properties.racking_area.title', 'Rack Area')
      : t<string>(
          'interface:properties.racking_area.title_plural',
          `Rack areas (${selectedRackingAreas.length})`,
          { amount: selectedRackingAreas.length },
        );

  return (
    <Accordion
      persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_RACKING}
      title={title}
      unmountOnExit
      sx={PANEL_ACCORDION_SX}
    >
      <Stack direction='column' spacing={FIELDS_SPACING} pb={FIELDS_PADDING_BOTTOM}>
        <PropertiesLabel i18nkey='interface:properties.area.rack.racking_type.label'>
          <RackingAreaType selectedRackingAreas={selectedRackingAreas} />
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.area.load_handling_type'>
          <RackingLoadHandlingType selectedRackingAreas={selectedRackingAreas} />
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.area.alignment'>
          <Alignment selectedRackingAreas={selectedRackingAreas} />
        </PropertiesLabel>
        <Margin selectedRackingAreas={selectedRackingAreas} />
        <OffsetDriving selectedRackingAreas={selectedRackingAreas} />
        <AlongsideExtension selectedRackingAreas={selectedRackingAreas} />
        <InsideExtension selectedRackingAreas={selectedRackingAreas} />
        <PropertiesLabel i18nkey='interface:properties.area.connect_neighbors'>
          <RackingConnectNeighbors selectedRackingAreas={selectedRackingAreas} />
        </PropertiesLabel>
        <Accordion
          persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_RACKING_LOAD_CARRIER_SETTINGS}
          titleKey='interface:properties.sections.load_carrier_settings'
          unmountOnExit
        >
          <Stack direction='column' spacing={FIELDS_SPACING}>
            <LoadType selectedAreas={selectedRackingAreas} />
          </Stack>
        </Accordion>
        <Accordion
          persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_RACKING_VEHICLE_SETTINGS}
          titleKey='interface:properties.sections.vehicle_settings'
          unmountOnExit
        >
          <Stack direction='column' spacing={FIELDS_SPACING}>
            <RackingPoiPreferences selectedRackingAreas={selectedRackingAreas} />
            <VehicleType selectedAreas={selectedRackingAreas} />
          </Stack>
        </Accordion>
      </Stack>
      <Divider />
      <RackSpec selectedRackingAreas={selectedRackingAreas} />
    </Accordion>
  );
}

export const RackingAreaParameters = memo(RackingAreaParametersComponent);
